import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { StatusChip } from '../../components/qaqc/DerivedFigure.js';
import { OfflineBanner } from '../../components/lesson/OfflineBanner.js';
import {
  fetchLessons,
  fetchReference,
  type Lesson,
  type LessonLogFilters,
} from '../../lib/lesson-api.js';
import { cacheLessons, readCachedLessons } from '../../lib/offline/outbox.js';
import { useOutbox } from '../../lib/offline/useOutbox.js';
import { t, formatDate } from '../../lib/i18n.js';

/**
 * The Lesson Learned Log (spec §10 key screens).
 *
 * The point of App 2 is that a lesson can be FOUND again — a register nobody
 * searches is a filing cabinet. So the search runs server-side across the
 * narrative fields, not only the title, and the filters mirror the ones the
 * spec names.
 *
 * Every successful load is mirrored to IndexedDB. Re-opening the log with no
 * signal then shows the last known list instead of an empty screen, clearly
 * labelled as the device's copy rather than presented as current.
 */
export function LessonLog() {
  const outbox = useOutbox();
  const [filters, setFilters] = useState<LessonLogFilters>({});
  const [search, setSearch] = useState('');
  const [cached, setCached] = useState<Lesson[] | null>(null);

  // Debounced so a typed query is one request rather than one per keystroke on
  // a connection that may be a bar of 3G.
  useEffect(() => {
    const timer = setTimeout(
      () => setFilters((current) => ({ ...current, search: search.trim() || undefined })),
      300,
    );
    return () => clearTimeout(timer);
  }, [search]);

  const reference = useQuery({
    queryKey: ['reference', 'lesson-learned'],
    queryFn: () => fetchReference('lesson-learned'),
    staleTime: 10 * 60_000,
  });

  const lessons = useQuery({
    queryKey: ['lessons', filters],
    queryFn: () => fetchLessons(filters),
    retry: false,
  });

  useEffect(() => {
    if (lessons.data) void cacheLessons(lessons.data.lessons as unknown as { id: string }[]);
  }, [lessons.data]);

  useEffect(() => {
    if (!lessons.isError) return;
    void readCachedLessons().then((rows) => setCached(rows as unknown as Lesson[]));
  }, [lessons.isError]);

  const rows = lessons.data?.lessons ?? cached ?? [];
  const showingCache = !lessons.data && cached !== null;

  const set = (key: keyof LessonLogFilters, value: string) =>
    setFilters((current) => ({ ...current, [key]: value || undefined }));

  return (
    <div className="min-h-full">
      <AppTopBar app="lesson-learned" />
      <OfflineBanner state={outbox} />
      <Breadcrumbs app="lesson-learned" trail={['Lesson Learned Log']} />

      <main className="mx-auto max-w-7xl px-4 pb-12 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="m-0 text-2xl font-semibold">{t("Lesson Learned Log")}</h1>
          <Link
            to="/lesson-learned/new"
            className="rounded-sm bg-ag-purple px-4 py-2 text-sm font-medium text-content-inverse no-underline"
          >
            {t("New lesson")}
          </Link>
        </div>

        <section className="mt-5 rounded-md border border-border bg-surface p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="sm:col-span-2">
              <label htmlFor="search" className="text-sm font-medium">
                {t("Search")}
              </label>
              <input
                id="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("Title, reference, or anything in the narrative")}
                className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label htmlFor="filter-project" className="text-sm font-medium">
                {t("Project")}
              </label>
              <select
                id="filter-project"
                value={filters.projectId ?? ''}
                onChange={(event) => set('projectId', event.target.value)}
                className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm"
              >
                <option value="">{t("All projects")}</option>
                {(reference.data?.projects ?? []).map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.code}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="filter-category" className="text-sm font-medium">
                {t("Severity")}
              </label>
              <select
                id="filter-category"
                value={filters.issueCategory ?? ''}
                onChange={(event) => set('issueCategory', event.target.value)}
                className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm"
              >
                <option value="">{t("Any")}</option>
                <option value="minor">{t("Minor")}</option>
                <option value="moderate">{t("Moderate")}</option>
                <option value="major">{t("Major")}</option>
              </select>
            </div>

            <div>
              <label htmlFor="filter-status" className="text-sm font-medium">
                {t("Status")}
              </label>
              <select
                id="filter-status"
                value={filters.status ?? ''}
                onChange={(event) => set('status', event.target.value)}
                className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm"
              >
                <option value="">{t("Any")}</option>
                <option value="draft">{t("Draft")}</option>
                <option value="submitted">{t("Submitted")}</option>
                <option value="sent_back">{t("Sent back")}</option>
                <option value="approved">{t("Approved")}</option>
              </select>
            </div>
          </div>
        </section>

        {showingCache && (
          <p role="status" className="mt-4 text-sm text-content-secondary">
            {t("Showing the copy held on this device. Reconnect to search the full log.")}
          </p>
        )}

        <section className="mt-5 overflow-x-auto rounded-md border border-border bg-surface">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{t("Lesson Learned records")}</caption>
            <thead>
              <tr className="border-b border-border text-start">
                <th scope="col" className="px-4 py-3 text-start font-semibold">
                  {t("Reference")}
                </th>
                <th scope="col" className="px-4 py-3 text-start font-semibold">
                  {t("Title")}
                </th>
                <th scope="col" className="px-4 py-3 text-start font-semibold">
                  {t("Severity")}
                </th>
                <th scope="col" className="px-4 py-3 text-start font-semibold">
                  {t("Impact")}
                </th>
                <th scope="col" className="px-4 py-3 text-start font-semibold">
                  {t("Captured")}
                </th>
                <th scope="col" className="px-4 py-3 text-start font-semibold">
                  {t("Status")}
                </th>
              </tr>
            </thead>
            <tbody>
              {lessons.isLoading && !cached && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-content-secondary">
                    {t("Loading…")}
                  </td>
                </tr>
              )}

              {!lessons.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-content-secondary">
                    {t("No lessons match. The first one recorded on a project starts its log.")}
                  </td>
                </tr>
              )}

              {rows.map((lesson) => (
                <tr key={lesson.id} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-3">
                    <Link
                      to={`/lesson-learned/lessons/${lesson.id}`}
                      className="font-medium no-underline"
                    >
                      {lesson.referenceNo}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{lesson.title}</td>
                  <td className="px-4 py-3 capitalize">{lesson.issueCategory}</td>
                  <td className="px-4 py-3 capitalize">{lesson.impact}</td>
                  <td className="px-4 py-3">
                    {lesson.capturedAt ? formatDate(lesson.capturedAt) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusChip status={lesson.status} />
                    {lesson.syncState === 'conflict' && (
                      <Link
                        to="/lesson-learned/conflicts"
                        className="ms-2 text-xs font-medium underline"
                      >
                        {t("needs reconciling")}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}
